import dayjs, { Dayjs } from 'dayjs';
import { TDateRange } from '../components';

const dayOfWeeks = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

export const formatHoursAsHourMin = (hours: number): string => {
  let min = Math.round(
    60 * parseFloat((hours - Math.floor(hours)).toFixed(2)),
  ).toString();
  if (min.length < 2) min = `0${min}`;

  return `${Math.floor(hours)}:${min}`;
};

export const formatInputAshours = (input: string) => {
  const mins = /^\d+(\d*|m)$/i;
  const hours = /^(\d*\.\d+|\d+\.\d*|\d+h)$/i;
  const time = /^(\d+:\d*|\d*:\d+)$/;

  let result = input;

  if (mins.test(input)) {
    const cleanString = input.includes('m') || input.includes('M')
      ? input.slice(0, input.length - 1)
      : input;

    result = (parseFloat(cleanString) / 60).toFixed(2);
  } else if (hours.test(input)) {
    result = input.includes('h') || input.includes('H')
      ? input.slice(0, input.length - 1)
      : input;
  } else if (time.test(input)) {
    if (input[input.length - 1] === ':') {
      result = input.slice(0, input.length - 1);
    } else if (input.startsWith(':')) {
      result = (parseFloat(input.slice(1)) / 60).toFixed(2);
    } else {
      const value = input.split(':');
      result = (parseFloat(value[0]) + parseFloat(value[1]) / 60).toFixed(2);
    }
  }

  return parseFloat(result);
};

// https://stackoverflow.com/a/51918448
export const firstDayOfWeek = (
  dateObject: Date,
  firstDayOfWeekIndex: keyof typeof dayOfWeeks | number,
): Date => {
  const firstDow = typeof firstDayOfWeekIndex === 'number'
    ? firstDayOfWeekIndex
    : dayOfWeeks[firstDayOfWeekIndex];
  const dayOfWeek = dateObject.getDay();
  const fDow = new Date(dateObject);
  const diff = dayOfWeek >= firstDow ? dayOfWeek - firstDow : 6 - dayOfWeek;

  fDow.setDate(dateObject.getDate() - diff);
  fDow.setHours(0, 0, 0, 0);

  return fDow;
};

export const getDateRange = (startDate: Date, endDate: Date): Date[] => {
  const results: Date[] = [];

  for (
    let dt = new Date(startDate);
    dt <= endDate;
    dt.setDate(dt.getDate() + 1)
  ) {
    results.push(new Date(dt));
  }

  return results;
};

export const getMonthDateRange = (date: Date | Dayjs) => {
  const results = {
    startDate: dayjs(date).startOf('month'),
    endDate: dayjs(date).endOf('month'),
    month: dayjs(date).month(),
    date: dayjs(date),
  };

  return results;
};

export const getDayOfWeek = (dow: number): string => {
  switch (dow) {
    case 0:
      return 'Sunday';
    case 1:
      return 'Monday';
    case 2:
      return 'Tuesday';
    case 3:
      return 'Wednesday';
    case 4:
      return 'Thursday';
    case 5:
      return 'Friday';
    case 6:
      return 'Saturday';
    default:
      return 'Unknown';
  }
};

export const formatDayOrdinalEndings = (value: Dayjs) => {
  const dayValue = parseInt(value.format('D'), 10);
  let result = `${value.format('dddd')} ${dayValue}`;

  if (dayValue > 3 && dayValue < 21) result += 'th';
  else {
    switch (dayValue % 10) {
      case 1:
        result += 'st';
        break;
      case 2:
        result += 'nd';
        break;
      case 3:
        result += 'rd';
        break;
      default:
        result += 'th';
    }
  }
  return result;
};

export const defaultDateRange = (
  startOfWeek:
    | number
    | 'sunday'
    | 'monday'
    | 'tuesday'
    | 'wednesday'
    | 'thursday'
    | 'friday'
    | 'saturday',
) => {
  const startDate = dayjs()
    .set('hour', 0)
    .set('minute', 0)
    .set('second', 0)
    .set('millisecond', 0);

  const startDow = dayjs(firstDayOfWeek(startDate.toDate(), startOfWeek));
  const endDow = startDow.add(6, 'day');

  const dateRange: TDateRange = { start: startDow, end: endDow };

  return dateRange;
};
